import {
  Currency,
  prisma,
  PaymentType,
  GatewayType,
  GatewayLevel,
  PaymentMethod,
  type Prisma,
} from "client";
import { invalidateEventPageCacheForEvent } from "../event/eventPageCache";

type GatewayMethodInput = {
  type: PaymentMethod;
};

type HyperpayConfigInput = {
  entityId: string;
  accessToken: string;
  entityIdSandbox: string;
  accessTokenSandbox: string;
};

type GatewayConfigurationInput = {
  id?: string;
  methods?: GatewayMethodInput[];
  hyperpay?: HyperpayConfigInput;
  wireTransfer?: Omit<Prisma.WireTransferUncheckedCreateInput, "id">;
  eFawateerCom?: Omit<Prisma.EFawateerComUncheckedCreateInput, "id">;
  cash?: Omit<Prisma.CashUncheckedCreateInput, "id">;
  free?: Omit<Prisma.FreeUncheckedCreateInput, "id">;
  cliq?: Omit<Prisma.CliqUncheckedCreateInput, "id">;
  pos?: Omit<Prisma.PosUncheckedCreateInput, "id">;
};

type GatewayMethodsUpdate = Prisma.GatewayConfigurationUpdateInput["methods"];

const gatewayConfigurationInclude = {
  methods: true,
  hyperpay: true,
  wireTransfer: true,
  eFawateerCom: true,
  cliq: true,
  pos: true,
  cash: true,
  free: true,
} as const;

const gatewayListInclude = {
  configurations: {
    include: gatewayConfigurationInclude,
  },
  whiteLabels: true,
  organizations: true,
} as const;

type GatewayWithConfigs = Prisma.PaymentGatewayGetPayload<{
  include: typeof gatewayListInclude;
}>;

export type PublicPaymentGatewaySource = "sajilni" | "white_label" | "organization";

export type PaymentGatewayListItem = Omit<
  GatewayWithConfigs,
  "configurations"
> & {
  configurations: Array<{
    id: string;
    methods: GatewayWithConfigs["configurations"][0]["methods"];
    hyperpay?: GatewayWithConfigs["configurations"][0]["hyperpay"];
    wireTransfer?: GatewayWithConfigs["configurations"][0]["wireTransfer"];
    eFawateerCom?: GatewayWithConfigs["configurations"][0]["eFawateerCom"];
    cliq?: GatewayWithConfigs["configurations"][0]["cliq"];
    pos?: GatewayWithConfigs["configurations"][0]["pos"];
    cash?: GatewayWithConfigs["configurations"][0]["cash"];
    free?: GatewayWithConfigs["configurations"][0]["free"];
  }>;
  source?: PublicPaymentGatewaySource;
  isEditable?: boolean;
};

function stripSensitiveConfiguration(
  config: GatewayWithConfigs["configurations"][0],
  includeOfflineDetails: boolean
) {
  const { methods, id } = config;
  if (includeOfflineDetails) {
    const {
      hyperpay: _h,
      eFawateerCom: _e,
      ...rest
    } = config;
    return { ...rest, id, methods };
  }
  return { id, methods };
}

export function toPublicPaymentGateway(
  gateway: GatewayWithConfigs,
  source: PublicPaymentGatewaySource,
  includeOfflineDetails = false
): PaymentGatewayListItem {
  const { configurations, ...rest } = gateway;
  return {
    ...rest,
    configurations: configurations.map((cfg) =>
      stripSensitiveConfiguration(cfg, includeOfflineDetails)
    ),
    source,
    isEditable: false,
  };
}

function splitOnlineOffline(gateways: PaymentGatewayListItem[]) {
  return {
    online: gateways.filter((g) => g.paymentType === PaymentType.ONLINE),
    offline: gateways.filter((g) => g.paymentType === PaymentType.OFFLINE),
  };
}

function toOwnedGateway(gateway: GatewayWithConfigs): PaymentGatewayListItem {
  return {
    ...gateway,
    isEditable: true,
  };
}

async function fetchGateways(where: Prisma.PaymentGatewayWhereInput) {
  return prisma.paymentGateway.findMany({
    where,
    include: gatewayListInclude,
  });
}

export const getSajilniPaymentGateway = async (params?: {
  currency?: Currency;
}) => {
  const { currency } = params ?? {};
  const gateways = await fetchGateways({
    level: GatewayLevel.SAJILNI,
    ...(currency && { currency }),
  });
  const items = gateways.map((g) => toOwnedGateway(g));
  return splitOnlineOffline(items);
};

export type GetSajilniPaymentGatewayResponseType = Awaited<
  ReturnType<typeof getSajilniPaymentGateway>
>;

export type PaymentGatewaysForWhiteLabelResponse = {
  inherited: GetSajilniPaymentGatewayResponseType;
  owned: GetSajilniPaymentGatewayResponseType;
};

export const getPaymentGatewaysForWhiteLabel = async (
  whiteLabelId: string,
  currency?: Currency
) => {
  const currencyFilter = currency ? { currency } : {};

  const [inheritedGateways, ownedGateways] = await Promise.all([
    fetchGateways({
      level: GatewayLevel.SAJILNI,
      whiteLabelIds: { has: whiteLabelId },
      ...currencyFilter,
    }),
    fetchGateways({
      level: GatewayLevel.WHITE_LABEL,
      ownerWhiteLabelId: whiteLabelId,
      ...currencyFilter,
    }),
  ]);

  const inherited = splitOnlineOffline(
    inheritedGateways.map((g) => toPublicPaymentGateway(g, "sajilni"))
  );
  const owned = splitOnlineOffline(ownedGateways.map(toOwnedGateway));

  return { inherited, owned };
};

export type PaymentGatewaysForOrganizationResponse = {
  inheritedSystem: GetSajilniPaymentGatewayResponseType;
  inheritedWl: GetSajilniPaymentGatewayResponseType;
  owned: GetSajilniPaymentGatewayResponseType;
};

export const getPaymentGatewaysForOrganization = async (
  organizationId: string,
  whiteLabelId: string,
  currency?: Currency
) => {
  const currencyFilter = currency ? { currency } : {};

  const [inheritedSystemGateways, inheritedWlGateways, ownedGateways] =
    await Promise.all([
      fetchGateways({
        level: GatewayLevel.SAJILNI,
        whiteLabelIds: { has: whiteLabelId },
        ...currencyFilter,
      }),
      fetchGateways({
        level: GatewayLevel.WHITE_LABEL,
        ownerWhiteLabelId: whiteLabelId,
        ...currencyFilter,
      }),
      fetchGateways({
        level: GatewayLevel.ORGANIZATION,
        ownerOrganizationId: organizationId,
        ...currencyFilter,
      }),
    ]);

  return {
    inheritedSystem: splitOnlineOffline(
      inheritedSystemGateways.map((g) => toPublicPaymentGateway(g, "sajilni"))
    ),
    inheritedWl: splitOnlineOffline(
      inheritedWlGateways.map((g) =>
        toPublicPaymentGateway(g, "white_label", true)
      )
    ),
    owned: splitOnlineOffline(ownedGateways.map(toOwnedGateway)),
  };
};

export const getAvailableGatewaysForEvent = async (
  whiteLabelId: string,
  organizationId?: string,
  currency?: Currency
) => {
  const currencyFilter = currency ? { currency } : {};
  const disabledFilter = { disabled: false };

  const scopeConditions: Prisma.PaymentGatewayWhereInput[] = [
    {
      level: GatewayLevel.SAJILNI,
      whiteLabelIds: { has: whiteLabelId },
    },
    {
      level: GatewayLevel.WHITE_LABEL,
      ownerWhiteLabelId: whiteLabelId,
    },
  ];

  if (organizationId) {
    scopeConditions.push({
      level: GatewayLevel.ORGANIZATION,
      ownerOrganizationId: organizationId,
    });
  }

  const gateways = await fetchGateways({
    ...disabledFilter,
    ...currencyFilter,
    OR: scopeConditions,
  });

  const items = gateways.map((g) => {
    if (g.level === GatewayLevel.SAJILNI) {
      return toPublicPaymentGateway(g, "sajilni", true);
    }
    if (g.level === GatewayLevel.WHITE_LABEL) {
      return toPublicPaymentGateway(g, "white_label", true);
    }
    return toPublicPaymentGateway(g, "organization", true);
  });

  return splitOnlineOffline(items);
};

export type PaymentGatewayEditContext = {
  level: GatewayLevel;
  whiteLabelId?: string;
  organizationId?: string;
};

export const getPaymentGatewayForEdit = async (
  gatewayId: string,
  context: PaymentGatewayEditContext
) => {
  const gateway = await prisma.paymentGateway.findUnique({
    where: { id: gatewayId },
    include: gatewayListInclude,
  });

  if (!gateway) return null;

  if (context.level === GatewayLevel.SAJILNI) {
    if (
      gateway.level !== GatewayLevel.SAJILNI ||
      gateway.ownerWhiteLabelId ||
      gateway.ownerOrganizationId
    ) {
      return null;
    }
    return gateway;
  }

  if (context.level === GatewayLevel.WHITE_LABEL) {
    if (
      !context.whiteLabelId ||
      gateway.level !== GatewayLevel.WHITE_LABEL ||
      gateway.ownerWhiteLabelId !== context.whiteLabelId
    ) {
      return null;
    }
    return gateway;
  }

  if (context.level === GatewayLevel.ORGANIZATION) {
    if (
      !context.organizationId ||
      gateway.level !== GatewayLevel.ORGANIZATION ||
      gateway.ownerOrganizationId !== context.organizationId
    ) {
      return null;
    }
    return gateway;
  }

  return null;
};

export function isPaymentGatewayInEventScope(
  gateway: {
    level: GatewayLevel;
    whiteLabelIds: string[];
    ownerWhiteLabelId: string | null;
    ownerOrganizationId: string | null;
    disabled: boolean;
  },
  whiteLabelId: string,
  organizationIds: string[]
) {
  if (gateway.disabled) return false;

  if (gateway.level === GatewayLevel.SAJILNI) {
    return gateway.whiteLabelIds.includes(whiteLabelId);
  }
  if (gateway.level === GatewayLevel.WHITE_LABEL) {
    return gateway.ownerWhiteLabelId === whiteLabelId;
  }
  if (gateway.level === GatewayLevel.ORGANIZATION) {
    return (
      !!gateway.ownerOrganizationId &&
      organizationIds.includes(gateway.ownerOrganizationId)
    );
  }
  return false;
}

export function isEventMethodConfigActive(
  config: {
    startActivationDate: Date | null;
    endActivationDate: Date | null;
  },
  now = new Date()
) {
  if (config.startActivationDate && now < config.startActivationDate) {
    return false;
  }
  if (config.endActivationDate && now > config.endActivationDate) {
    return false;
  }
  return true;
}

// Legacy helpers — scoped to SAJILNI + WL assignment
export const getPaymentGateway = async (id: string, organizerId?: string) => {
  const response = await prisma.paymentGateway.findMany({
    where: {
      type: GatewayType.HYPERPAY,
      OR: [
        {
          level: GatewayLevel.SAJILNI,
          whiteLabelIds: { hasSome: [id] },
        },
        ...(organizerId
          ? [
            {
              level: GatewayLevel.ORGANIZATION,
              ownerOrganizationId: organizerId,
            },
          ]
          : []),
        ],
    },
    include: {
      whiteLabels: true,
    },
  });
  return response;
};

export const getOfflinePaymentGateway = async (
  id: string,
  organizerId?: string
) => {
  const response = await prisma.paymentGateway.findMany({
    where: {
      paymentType: PaymentType.OFFLINE,
      OR: [
        {
          level: GatewayLevel.SAJILNI,
          whiteLabelIds: { hasSome: [id] },
        },
        ...(organizerId
          ? [
            {
              level: GatewayLevel.ORGANIZATION,
              ownerOrganizationId: organizerId,
            },
          ]
          : []),
        ],
    },
    include: {
      whiteLabels: true,
    },
  });
  return response;
};

export const getOnlinePaymentGateway = async (
  whiteLabelId?: string,
  organizerId?: string
) => {
  const response = await prisma.paymentGateway.findMany({
    where: {
      paymentType: PaymentType.ONLINE,
      OR: [
        ...(whiteLabelId
          ? [
            {
              level: GatewayLevel.SAJILNI,
              whiteLabelIds: { hasSome: [whiteLabelId] },
            },
            {
              level: GatewayLevel.WHITE_LABEL,
              ownerWhiteLabelId: whiteLabelId,
            },
          ]
          : []),
        ...(organizerId
          ? [
            {
              level: GatewayLevel.ORGANIZATION,
              ownerOrganizationId: organizerId,
            },
          ]
          : []),
      ],
    },
    include: {
      configurations: {
        include: {
          methods: true,
          hyperpay: true,
          wireTransfer: true,
        },
      },
    },
  });

  return response;
};

export const getAllOfflineActivePaymentGateway = async (id: string) => {
  const response = await prisma.paymentGateway.findMany({
    where: {
      paymentType: PaymentType.OFFLINE,
      disabled: false,
      OR: [
        {
          level: GatewayLevel.SAJILNI,
          whiteLabelIds: { hasSome: [id] },
        },
        {
          level: GatewayLevel.WHITE_LABEL,
          ownerWhiteLabelId: id,
        },
      ],
    },
  });
  return response;
};

export const getPaymentGatewayByType = async (
  id: string,
  type: PaymentType
) => {
  const response = await prisma.paymentGateway.findMany({
    where: {
      paymentType: type,
      OR: [
        { level: GatewayLevel.SAJILNI, whiteLabelIds: { hasSome: [id] } },
        { level: GatewayLevel.WHITE_LABEL, ownerWhiteLabelId: id },
      ],
    },
  });
  return response;
};

export const getPaymentGatewayGroupedByPaymentType = async (
  _level: GatewayLevel,
  id: string
) => {
  const response = await prisma.paymentGateway.groupBy({
    by: ["paymentType"],
    where: {
      OR: [
        { level: GatewayLevel.SAJILNI, whiteLabelIds: { hasSome: [id] } },
        { level: GatewayLevel.WHITE_LABEL, ownerWhiteLabelId: id },
        { level: GatewayLevel.ORGANIZATION, ownerOrganizationId: id },
      ],
    },
  });
  return response;
};

// update
export const updatePaymentGateway = async (
  paymentGatewayBody: {
    label?: string;
    paymentType?: PaymentType;
    level?: GatewayLevel;
    type?: GatewayType;
    currency?: Currency;
    disabled?: boolean;
    whiteLabelIds?: string[];
    organizationIds?: string[];
    ownerWhiteLabelId?: string;
    ownerOrganizationId?: string;
    configurations?: GatewayConfigurationInput[];
  },
  id: string
) => {
  try {
    const configsToUpdate =
      paymentGatewayBody.configurations?.filter((cfg) => cfg.id) || [];
    const configsToCreate =
      paymentGatewayBody.configurations?.filter((cfg) => !cfg.id) || [];

    const configUpdates = await Promise.all(
      configsToUpdate.map(async (cfg: GatewayConfigurationInput) => {
        let methodsUpdate: GatewayMethodsUpdate | undefined = undefined;

        if (cfg.methods) {
          const existingConfig = await prisma.gatewayConfiguration.findUnique({
            where: { id: cfg.id },
            include: { methods: true },
          });

          if (existingConfig?.methods) {
            const existingMethods = existingConfig.methods;
            const newMethodTypes = new Set(
              cfg.methods.map((m: GatewayMethodInput) => m.type)
            );

            const methodsToKeep = existingMethods.filter((m) =>
              newMethodTypes.has(m.type)
            );
            const keptMethodTypes = new Set(methodsToKeep.map((m) => m.type));

            const methodsToDisconnect = existingMethods.filter(
              (m) => !newMethodTypes.has(m.type)
            );

            const methodsToCreate = cfg.methods
              .filter((m: GatewayMethodInput) => !keptMethodTypes.has(m.type))
              .map((m: GatewayMethodInput) => ({ type: m.type }));

            methodsUpdate = {
              ...(methodsToDisconnect.length > 0 && {
                disconnect: methodsToDisconnect.map((m) => ({ id: m.id })),
              }),
              ...(methodsToCreate.length > 0 && {
                create: methodsToCreate,
              }),
            };
          } else {
            methodsUpdate = {
              create: cfg.methods.map((m: GatewayMethodInput) => ({
                type: m.type,
              })),
            };
          }
        }

        return {
          where: { id: cfg.id },
          data: {
            methods: methodsUpdate,
          },
        };
      })
    );

    const response = await prisma.paymentGateway.update({
      where: { id },
      data: {
        label: paymentGatewayBody.label,
        paymentType: paymentGatewayBody.paymentType as PaymentType | undefined,
        level: paymentGatewayBody.level as GatewayLevel | undefined,
        type: paymentGatewayBody.type,
        currency: paymentGatewayBody.currency,
        disabled: paymentGatewayBody.disabled,
        ownerWhiteLabelId: paymentGatewayBody.ownerWhiteLabelId,
        ownerOrganizationId: paymentGatewayBody.ownerOrganizationId,
        whiteLabels: paymentGatewayBody.whiteLabelIds
          ? { set: paymentGatewayBody.whiteLabelIds.map((wlId) => ({ id: wlId })) }
          : undefined,
        configurations:
          configUpdates.length > 0
            ? {
              update: configUpdates,
            }
            : undefined,
      },
      include: gatewayListInclude,
    });

    for (const cfg of configsToUpdate) {
      const existingConfig = await prisma.gatewayConfiguration.findUnique({
        where: { id: cfg.id },
        select: { gatewayId: true },
      });

      if (!existingConfig) continue;

      const gatewayId = existingConfig.gatewayId;

      if (cfg.hyperpay) {
        await prisma.hyperpay.upsert({
          where: { id: gatewayId },
          create: {
            id: gatewayId,
            entityId: cfg.hyperpay.entityId,
            accessToken: cfg.hyperpay.accessToken,
            entityIdSandbox: cfg.hyperpay.entityIdSandbox,
            accessTokenSandbox: cfg.hyperpay.accessTokenSandbox,
          },
          update: {
            entityId: cfg.hyperpay.entityId,
            accessToken: cfg.hyperpay.accessToken,
            entityIdSandbox: cfg.hyperpay.entityIdSandbox,
            accessTokenSandbox: cfg.hyperpay.accessTokenSandbox,
          },
        });
      }

      if (cfg.wireTransfer) {
        await prisma.wireTransfer.upsert({
          where: { id: gatewayId },
          create: {
            id: gatewayId,
            accountHolderName: cfg.wireTransfer.accountHolderName,
            bankName: cfg.wireTransfer.bankName,
            ibanNumber: cfg.wireTransfer.ibanNumber,
            bankAccountNumber: cfg.wireTransfer.bankAccountNumber,
            bankBranch: cfg.wireTransfer.bankBranch,
            bankAddress: cfg.wireTransfer.bankAddress,
            bankCity: cfg.wireTransfer.bankCity,
            bankCountry: cfg.wireTransfer.bankCountry,
            swiftCode: cfg.wireTransfer.swiftCode,
          },
          update: {
            accountHolderName: cfg.wireTransfer.accountHolderName,
            bankName: cfg.wireTransfer.bankName,
            ibanNumber: cfg.wireTransfer.ibanNumber,
            bankAccountNumber: cfg.wireTransfer.bankAccountNumber,
            bankBranch: cfg.wireTransfer.bankBranch,
            bankAddress: cfg.wireTransfer.bankAddress,
            bankCity: cfg.wireTransfer.bankCity,
            bankCountry: cfg.wireTransfer.bankCountry,
            swiftCode: cfg.wireTransfer.swiftCode,
          },
        });
      }

      if (cfg.eFawateerCom) {
        const { createdAt, updatedAt, ...eFawateerComData } = cfg.eFawateerCom;
        await prisma.eFawateerCom.upsert({
          where: { id: gatewayId },
          create: {
            id: gatewayId,
            ...eFawateerComData,
          },
          update: {
            ...eFawateerComData,
          },
        });
      }

      if (cfg.cliq) {
        await prisma.cliq.upsert({
          where: { id: gatewayId },
                create: {
            id: gatewayId,
            alias: cfg.cliq.alias,
            accountHolderName: cfg.cliq.accountHolderName,
            accountHolderNumber: cfg.cliq.accountHolderNumber,
            bankName: cfg.cliq.bankName,
            instructions: cfg.cliq.instructions,
          },
          update: {
            alias: cfg.cliq.alias,
            accountHolderName: cfg.cliq.accountHolderName,
            accountHolderNumber: cfg.cliq.accountHolderNumber,
            bankName: cfg.cliq.bankName,
            instructions: cfg.cliq.instructions,
          },
        });
      }

      if (cfg.pos) {
        await prisma.pos.upsert({
          where: { id: gatewayId },
          create: {
            id: gatewayId,
            instructions: cfg.pos.instructions,
          },
          update: {
            instructions: cfg.pos.instructions,
          },
        });
      }
    }

    if (configsToCreate.length > 0) {
      for (const cfg of configsToCreate) {
        await createGatewayConfigurationWithDetails(prisma, id, cfg);
      }
    }

    return response;
  } catch (error: unknown) {
    console.error(error);
    throw new Error(
      error instanceof Error ? error.message : "Failed to update payment gateway"
    );
  }
};

interface PaymentMethodConfig {
  gatewayMethodId: string;
  startActivationDate?: Date | string;
  endActivationDate?: Date | string;
  paymentExpiryDurationInMinutes?: number;
  disabled?: boolean;
}

export const updateEventPaymentMethod = async (
  eventId: string,
  paymentMethods: PaymentMethodConfig[]
) => {
  try {
    const event = await prisma.event.findUnique({
      where: { id: eventId },
      select: {
        whiteLabelId: true,
        organizationIds: true,
        slug: true,
      },
    });

    if (!event) {
      throw new Error("Event not found");
    }

    if (paymentMethods.length > 0) {
      const gatewayMethodIds = paymentMethods.map((m) => m.gatewayMethodId);
      const gatewayMethods = await prisma.gatewayMethod.findMany({
        where: { id: { in: gatewayMethodIds } },
        select: {
          id: true,
          gatewayConfiguration: {
            select: {
              paymentGateway: {
                select: {
                  level: true,
                  whiteLabelIds: true,
                  ownerWhiteLabelId: true,
                  ownerOrganizationId: true,
                  disabled: true,
                },
              },
            },
          },
        },
      });

      const methodMap = new Map(gatewayMethods.map((m) => [m.id, m]));
      const outOfScope: string[] = [];

      for (const method of paymentMethods) {
        const gatewayMethod = methodMap.get(method.gatewayMethodId);
        const paymentGateway =
          gatewayMethod?.gatewayConfiguration?.paymentGateway;

        if (
          !paymentGateway ||
          !isPaymentGatewayInEventScope(
            paymentGateway,
            event.whiteLabelId,
            event.organizationIds
          )
        ) {
          outOfScope.push(method.gatewayMethodId);
        }
      }

      if (outOfScope.length > 0) {
        throw new Error(
          `Payment methods are not available for this event: ${outOfScope.join(", ")}`
        );
      }
    }

    const existingConfigs = await prisma.eventMethodConfig.findMany({
      where: { eventId },
      include: { gatewayMethod: true },
    });

    const existingConfigMap = new Map(
      existingConfigs.map((config) => [config.gatewayMethodId, config])
    );

    const selectedMethodIds = new Set(
      paymentMethods.map((method) => method.gatewayMethodId)
    );

    for (const existingConfig of existingConfigs) {
      if (!selectedMethodIds.has(existingConfig.gatewayMethodId)) {
        await prisma.eventMethodConfig.update({
          where: { id: existingConfig.id },
          data: {
            disabled: true,
            updatedAt: new Date(),
            paymentExpiryDurationInMinutes:
              existingConfig.paymentExpiryDurationInMinutes,
          },
        });
      }
    }

    for (const method of paymentMethods) {
      const existingConfig = existingConfigMap.get(method.gatewayMethodId);

      const configData = {
        startActivationDate: method.startActivationDate
          ? new Date(method.startActivationDate)
          : null,
        endActivationDate: method.endActivationDate
          ? new Date(method.endActivationDate)
          : null,
        ...(method.paymentExpiryDurationInMinutes !== undefined && {
          paymentExpiryDurationInMinutes:
            method.paymentExpiryDurationInMinutes,
        }),
        disabled: method.disabled || false,
      };

      if (existingConfig) {
        await prisma.eventMethodConfig.update({
          where: { id: existingConfig.id },
          data: configData,
        });
      } else {
        await prisma.eventMethodConfig.create({
          data: {
            eventId,
            gatewayMethodId: method.gatewayMethodId,
            ...configData,
          },
        });
      }
    }

    const eventSlug = event.slug;
    await invalidateEventPageCacheForEvent(eventId, eventSlug);

    return { success: true, message: "Payment methods updated successfully" };
  } catch (error) {
    console.error("Error updating payment methods:", error);
    throw error;
  }
};

type PaymentGatewayTx = Prisma.TransactionClient;

async function createGatewayConfigurationWithDetails(
  tx: PaymentGatewayTx,
  paymentGatewayId: string,
  cfg: GatewayConfigurationInput
) {
  const methodsData = cfg.methods?.length
    ? {
      create: cfg.methods.map((m: GatewayMethodInput) => ({ type: m.type })),
    }
    : undefined;

  const createConfig = (gatewayId: string) =>
    tx.gatewayConfiguration.create({
      data: {
        gatewayId,
        paymentGatewayId,
        methods: methodsData,
      },
    });

  if (cfg.hyperpay) {
    const record = await tx.hyperpay.create({
      data: {
                    entityId: cfg.hyperpay.entityId,
                    accessToken: cfg.hyperpay.accessToken,
                    entityIdSandbox: cfg.hyperpay.entityIdSandbox,
                    accessTokenSandbox: cfg.hyperpay.accessTokenSandbox,
                  },
    });
    return createConfig(record.id);
  }

  if (cfg.wireTransfer) {
    const record = await tx.wireTransfer.create({
      data: { ...cfg.wireTransfer },
    });
    return createConfig(record.id);
  }

  if (cfg.eFawateerCom) {
    const record = await tx.eFawateerCom.create({
      data: { ...cfg.eFawateerCom },
    });
    return createConfig(record.id);
  }

  if (cfg.cash) {
    const record = await tx.cash.create({ data: { ...cfg.cash } });
    return createConfig(record.id);
  }

  if (cfg.free) {
    const record = await tx.free.create({ data: { ...cfg.free } });
    return createConfig(record.id);
  }

  if (cfg.cliq) {
    const record = await tx.cliq.create({ data: { ...cfg.cliq } });
    return createConfig(record.id);
  }

  if (cfg.pos) {
    const record = await tx.pos.create({ data: { ...cfg.pos } });
    return createConfig(record.id);
  }

  throw new Error(
    "Gateway configuration requires a payment method detail record"
  );
}

export const createPaymentGateway = async (body: {
  label: string;
  paymentType: PaymentType;
  level: GatewayLevel;
  type: GatewayType;
  currency: Currency;
  disabled?: boolean;
  whiteLabelIds?: string[];
  organizationIds?: string[];
  ownerWhiteLabelId?: string;
  ownerOrganizationId?: string;
  configurations?: GatewayConfigurationInput[];
}) => {
  try {
    const paymentGatewayId = await prisma.$transaction(async (tx) => {
      const paymentGateway = await tx.paymentGateway.create({
        data: {
          label: body.label,
          paymentType: body.paymentType,
          level: body.level,
          type: body.type,
          currency: body.currency,
          disabled: body.disabled ?? false,
          ownerWhiteLabelId: body.ownerWhiteLabelId ?? null,
          ownerOrganizationId: body.ownerOrganizationId ?? null,
          whiteLabels: body.whiteLabelIds?.length
            ? {
              connect: body.whiteLabelIds.map((wlId: string) => ({ id: wlId })),
                }
                : undefined,
      },
      });

      for (const cfg of body.configurations ?? []) {
        await createGatewayConfigurationWithDetails(
          tx,
          paymentGateway.id,
          cfg
        );
      }

      return paymentGateway.id;
    });

    return prisma.paymentGateway.findUniqueOrThrow({
      where: { id: paymentGatewayId },
      include: gatewayListInclude,
    });
  } catch (error: unknown) {
    throw new Error(
      error instanceof Error ? error.message : "Failed to create payment gateway"
    );
  }
};

export const getIsDelayedPaymentGateway = async (
  methodId: string,
  tx?: Prisma.TransactionClient
) => {
  const db = tx ?? prisma;

  const method = await db.gatewayMethod.findUnique({
    where: { id: methodId },
    select: {
      gatewayConfiguration: {
        select: {
          paymentGateway: { select: { paymentType: true, type: true } },
          eFawateerCom: { select: { id: true } },
        },
      },
    },
  });

  if (!method?.gatewayConfiguration?.paymentGateway)
    return { isDelayedPaymentGateway: false, isEFawateerComGateway: false };

  const { paymentGateway, eFawateerCom } = method.gatewayConfiguration;

  const isOfflineGateway = paymentGateway.paymentType === PaymentType.OFFLINE;
  const isEFawateerComGateway =
    paymentGateway.type === GatewayType.EFAWATEERCOM && !!eFawateerCom;

  return {
    isDelayedPaymentGateway: isOfflineGateway || isEFawateerComGateway,
    isEFawateerComGateway,
  };
};

export async function assertGatewayMethodAllowedForEventCheckout(
  gatewayMethodId: string,
  eventId: string
) {
  const event = await prisma.event.findUnique({
    where: { id: eventId },
    select: {
      whiteLabelId: true,
      organizationIds: true,
    },
  });

  if (!event) {
    throw new Error("Event not found");
  }

  const eventMethodConfig = await prisma.eventMethodConfig.findFirst({
    where: {
      eventId,
      gatewayMethodId,
      disabled: false,
    },
    include: {
      gatewayMethod: {
        include: {
          gatewayConfiguration: {
            include: {
              paymentGateway: true,
            },
          },
        },
      },
    },
  });

  if (!eventMethodConfig) {
    throw new Error("Payment method is not enabled for this event");
  }

  if (!isEventMethodConfigActive(eventMethodConfig)) {
    throw new Error("Payment method is not currently active for this event");
  }

  const paymentGateway =
    eventMethodConfig.gatewayMethod.gatewayConfiguration?.paymentGateway;

  if (
    !paymentGateway ||
    paymentGateway.disabled ||
    !isPaymentGatewayInEventScope(
      paymentGateway,
      event.whiteLabelId,
      event.organizationIds
    )
  ) {
    throw new Error("Payment method is not available for this event");
  }

  return eventMethodConfig;
}
